import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import { AppButton, AppTextField, AppToggleField } from "../../../components/form";
import JsonCodeEditor from "../../../components/JsonCodeEditor";
import {
  AppFormField,
  AppFormGrid,
  PageFilterPanel,
  PageSectionCard,
} from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { Autocomplete } from "@mui/material";
import { GRID_FULL, GRID_HALF, formRowButtonSx } from "../../../lib/ui/formLayout";
import {
  adminIngestService,
  type IngestIndexGrant,
  type IndexSchema,
  type PiiRule,
} from "../../../services/adminIngestService";
import { ingestService } from "../../../services/ingestService";
import AdminOpenSearchPolicyTab from "./AdminOpenSearchPolicyTab";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { getCurrentUserRoles, isWorkspaceAdmin } from "../../../utils/authorization";

export default function AdminIngestPage() {
  const { t } = useTranslation();
  const packageLimits = usePackageLimits();
  const custom = useAppCustomStyles();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [piiRules, setPiiRules] = useState<PiiRule[]>([]);
  const [grants, setGrants] = useState<IngestIndexGrant[]>([]);
  const [schemas, setSchemas] = useState<IndexSchema[]>([]);

  const [piiPattern, setPiiPattern] = useState("email|phone|msisdn");
  const [piiStrategy, setPiiStrategy] = useState<"REDACT" | "HASH" | "PARTIAL">("REDACT");
  const [grantIndex, setGrantIndex] = useState("");
  const [schemaIndex, setSchemaIndex] = useState("");
  const [schemaJson, setSchemaJson] = useState('{"required":["id"]}');
  const [clusterIndices, setClusterIndices] = useState<string[]>([]);
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => setCanManage(isWorkspaceAdmin(roles)));
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, g, s, idx] = await Promise.all([
        adminIngestService.listPiiRules(),
        adminIngestService.listGrants(),
        adminIngestService.listSchemas(),
        ingestService.listIndices(),
      ]);
      setPiiRules(p);
      setGrants(g);
      setSchemas(s);
      setClusterIndices(idx.indices ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const addPiiRule = async () => {
    await adminIngestService.createPiiRule({
      fieldPattern: piiPattern,
      strategy: piiStrategy,
      active: true,
    });
    await load();
  };

  const addGrant = async () => {
    await adminIngestService.createGrant({
      indexName: grantIndex.trim(),
      environment: "STAGING",
      allowedRoles: ["user", "tenant_admin"],
      active: true,
    });
    setGrantIndex("");
    await load();
  };

  const addSchema = async () => {
    await adminIngestService.createSchema({
      indexName: schemaIndex.trim(),
      jsonSchema: schemaJson,
      version: 1,
      active: true,
    });
    setSchemaIndex("");
    await load();
  };

  const deleteGrant = async (id: string, itemName?: string) => {
    try {
      await confirmDelete({ itemName });
      await adminIngestService.deleteGrant(id);
      await load();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const deletePiiRule = async (id: string, itemName?: string) => {
    try {
      await confirmDelete({ itemName });
      await adminIngestService.deletePiiRule(id);
      await load();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const deleteSchema = async (id: string, itemName?: string) => {
    try {
      await confirmDelete({ itemName });
      await adminIngestService.deleteSchema(id);
      await load();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  return (
    <PageListContainer surface="plain">
      <PageHeader title={t("adminIngest.title")} subtitle={t("adminIngest.subtitle")} />
      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxIngestSettingsItemsPerTab != null}>
          {t("packageLimits.ingestItemsPerTab", {
            count: formatLimitValue(
              packageLimits.maxIngestSettingsItemsPerTab,
              t("packageLimits.unlimited")
            ),
          })}
        </PackageLimitHint>
      )}
      {error && <Alert severity="error" sx={custom.pages.shared.alertMarginBottom2}>{error}</Alert>}

      {canManage ? (
        <Box sx={custom.common.marginBottomTwo}>
          <AppButton
            variant="outlined"
            size="small"
            onClick={async () => {
              const res = await adminIngestService.syncIndexRegistry();
              window.alert(t("adminIngest.syncRegistryDone", { count: res.syncedJobs }));
              await load();
            }}
          >
            {t("adminIngest.syncRegistry")}
          </AppButton>
        </Box>
      ) : (
        <Alert severity="info" sx={custom.pages.shared.alertMarginBottom2}>
          {t("adminIngest.readOnlyNotice")}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={custom.common.tabsMarginBottomTwo}>
        <Tab icon={<VpnKeyOutlinedIcon fontSize="small" />} iconPosition="start" label={t("adminIngest.tabGrants")} />
        <Tab icon={<PrivacyTipOutlinedIcon fontSize="small" />} iconPosition="start" label={t("adminIngest.tabPii")} />
        <Tab icon={<SchemaOutlinedIcon fontSize="small" />} iconPosition="start" label={t("adminIngest.tabSchemas")} />
        {canManage && (
            <Tab
              icon={<PolicyOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t("adminIngest.tabOpenSearchPolicy")}
            />
        )}
      </Tabs>

      {tab === 0 && (
        <Box>
          {canManage && (
            <PageFilterPanel>
              <AppFormField size={{ xs: 12, md: "grow" }}>
                <Autocomplete
                  freeSolo
                  options={clusterIndices}
                  value={grantIndex}
                  inputValue={grantIndex}
                  onInputChange={(_, value) => setGrantIndex(value)}
                  onChange={(_, value) => setGrantIndex(typeof value === "string" ? value : value ?? "")}
                  renderInput={(params) => (
                    <AppTextField {...params} label={t("adminIngest.grantIndex")} />
                  )}
                />
              </AppFormField>
              <AppFormField size={{ xs: 12, md: "auto" }}>
                <AppButton variant="contained" onClick={addGrant} disabled={!grantIndex.trim()} sx={formRowButtonSx}>
                  {t("common.create")}
                </AppButton>
              </AppFormField>
            </PageFilterPanel>
          )}
          <PageSectionCard title={t("adminIngest.tabGrants")} noPadding>
            <Box sx={custom.pages.shared.overflowAuto}>
              <GrantTable grants={grants} onDelete={canManage ? (g) => deleteGrant(g.id!, g.indexName) : undefined} t={t} />
            </Box>
          </PageSectionCard>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {canManage && (
            <PageFilterPanel>
              <AppFormField size={{ xs: 12, md: "grow" }}>
                <AppTextField
                  label={t("adminIngest.piiPattern")}
                  value={piiPattern}
                  onChange={(e) => setPiiPattern(e.target.value)}
                />
              </AppFormField>
              <AppFormField size={{ xs: 12, md: 4 }}>
                <AppToggleField
                  label={t("adminIngest.piiStrategy")}
                  value={piiStrategy}
                  onChange={(value) => setPiiStrategy(value as typeof piiStrategy)}
                  options={[
                    { value: "REDACT", label: t("adminIngest.strategyRedact") },
                    { value: "HASH", label: t("adminIngest.strategyHash") },
                    { value: "PARTIAL", label: t("adminIngest.strategyPartial") },
                  ]}
                />
              </AppFormField>
              <AppFormField size={{ xs: 12, md: "auto" }}>
                <AppButton variant="contained" onClick={addPiiRule} sx={formRowButtonSx}>
                  {t("common.create")}
                </AppButton>
              </AppFormField>
            </PageFilterPanel>
          )}
          <PageSectionCard title={t("adminIngest.tabPii")} noPadding>
            <Box sx={custom.pages.shared.overflowAuto}>
              <PiiTable rules={piiRules} onDelete={canManage ? (r) => deletePiiRule(r.id!, r.fieldPattern) : undefined} t={t} />
            </Box>
          </PageSectionCard>
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {canManage && (
            <PageSectionCard title={t("adminIngest.schemaFormTitle")}>
              <AppFormGrid>
                <AppFormField size={GRID_FULL}>
                  <Autocomplete
                    freeSolo
                    options={clusterIndices}
                    value={schemaIndex}
                    inputValue={schemaIndex}
                    onInputChange={(_, value) => setSchemaIndex(value)}
                    onChange={(_, value) => setSchemaIndex(typeof value === "string" ? value : value ?? "")}
                    renderInput={(params) => (
                      <AppTextField {...params} label={t("adminIngest.schemaIndex")} />
                    )}
                  />
                </AppFormField>
                <AppFormField size={GRID_FULL}>
                  <JsonCodeEditor
                    label={t("adminIngest.schemaJson")}
                    value={schemaJson}
                    onChange={setSchemaJson}
                    height={200}
                  />
                </AppFormField>
                <AppFormField size={GRID_FULL}>
                  <AppButton variant="contained" onClick={addSchema} disabled={!schemaIndex.trim()} sx={formRowButtonSx}>
                    {t("common.create")}
                  </AppButton>
                </AppFormField>
              </AppFormGrid>
            </PageSectionCard>
          )}
          <PageSectionCard title={t("adminIngest.tabSchemas")} noPadding>
            <Box sx={custom.pages.shared.overflowAuto}>
              <SchemaTable schemas={schemas} onDelete={canManage ? (s) => deleteSchema(s.id!, s.indexName) : undefined} t={t} />
            </Box>
          </PageSectionCard>
        </Box>
      )}

      {canManage && tab === 3 && <AdminOpenSearchPolicyTab clusterIndices={clusterIndices} />}
    </PageListContainer>
  );
}

function GrantTable({
  grants,
  onDelete,
  t,
}: {
  grants: IngestIndexGrant[];
  onDelete?: (grant: IngestIndexGrant) => void;
  t: (k: string) => string;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("ingest.colIndex")}</TableCell>
          <TableCell>{t("ingest.colEnv")}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {grants.map((g) => (
          <TableRow key={g.id}>
            <TableCell>{g.indexName}</TableCell>
            <TableCell>{g.environment}</TableCell>
            <TableCell align="right">
              {g.id && onDelete && (
                <AppIconButton size="small" onClick={() => onDelete(g)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </AppIconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PiiTable({
  rules,
  onDelete,
  t,
}: {
  rules: PiiRule[];
  onDelete?: (rule: PiiRule) => void;
  t: (k: string) => string;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("adminIngest.piiPattern")}</TableCell>
          <TableCell>{t("adminIngest.piiStrategy")}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {rules.map((r) => (
          <TableRow key={r.id}>
            <TableCell>{r.fieldPattern}</TableCell>
            <TableCell>{r.strategy}</TableCell>
            <TableCell align="right">
              {r.id && onDelete && (
                <AppIconButton size="small" onClick={() => onDelete(r)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </AppIconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SchemaTable({
  schemas,
  onDelete,
  t,
}: {
  schemas: IndexSchema[];
  onDelete?: (schema: IndexSchema) => void;
  t: (k: string) => string;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("ingest.colIndex")}</TableCell>
          <TableCell>v</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {schemas.map((s) => (
          <TableRow key={s.id}>
            <TableCell>{s.indexName}</TableCell>
            <TableCell>{s.version}</TableCell>
            <TableCell align="right">
              {s.id && onDelete && (
                <AppIconButton size="small" onClick={() => onDelete(s)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </AppIconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
